import { CheckCircle2, FileText, XCircle } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import { formatDate } from "./status-chip"
import type { DocumentRow } from "./types"

/**
 * Every file that arrived about this work order, with the verdict on it.
 *
 * THE LLM EXTRACTS; RUBY JUDGES. The verification badge is the output of
 * deterministic rules run over the extraction (a replaced valve requires a
 * bubble-leak test line), never the model's opinion of its own work — which is
 * why a failure can list SPECIFIC problems instead of a confidence score.
 */
export function DocumentsStrip({ documents }: { documents: DocumentRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents</CardTitle>
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No documents attached yet.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {documents.map((doc) => (
              <li key={doc.id}>
                <a
                  href={doc.preview_url ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  className={
                    doc.preview_url
                      ? "hover:border-primary/50 block rounded-lg border p-3 transition-colors"
                      : "block cursor-default rounded-lg border p-3 opacity-70"
                  }
                >
                  <div className="flex items-center gap-2">
                    <FileText className="text-muted-foreground size-4 shrink-0" />
                    <span className="max-w-48 truncate text-sm font-medium">
                      {doc.filename ?? doc.doc_type_label}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline">{doc.doc_type_label}</Badge>
                    <VerificationBadge doc={doc} />
                  </div>
                  <p className="text-muted-foreground mt-1.5 text-xs">
                    {formatDate(doc.created_at)}
                  </p>
                </a>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

export function VerificationBadge({ doc }: { doc: DocumentRow }) {
  if (doc.verification_status === "pass") {
    return (
      <Badge className="border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/50 dark:text-emerald-200">
        <CheckCircle2 className="mr-1 size-3" />
        Verified
      </Badge>
    )
  }

  if (doc.verification_status === "fail") {
    // The problems are the point. A release form that failed because it's
    // missing the bubble-leak test line is actionable; "failed" alone is not.
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="destructive">
            <XCircle className="mr-1 size-3" />
            Failed check
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {doc.verification_problems.length > 0 ? (
            <ul className="list-inside list-disc space-y-0.5">
              {doc.verification_problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          ) : (
            "Verification failed — no detail recorded."
          )}
        </TooltipContent>
      </Tooltip>
    )
  }

  return null
}
